import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, TextField, Button,
  RadioGroup, FormControlLabel, Radio, Autocomplete, Alert, CircularProgress, Chip,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { colors } from '../../styles/theme.js';
import { api } from '../../api/client.js';
import {
  CREATE_LEVELS, type CreateDomainResponse, type CreateLevel, type WorkspaceRefOption,
} from '../../../shared/watchdogTypes.js';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Vorbefuellung aus dem GTM-Analyst (Seiten-URL oder Hostname). */
  initialHostname?: string;
  /** Wird nach erfolgreichem Anlegen gerufen, z. B. um die Domains-Liste neu zu laden. */
  onCreated?: (result: CreateDomainResponse) => void;
}

/** Nimmt volle URLs entgegen und zeigt im Feld nur den Hostname. */
function toHostname(input: string): string {
  const raw = (input || '').trim();
  if (!raw) return '';
  try {
    if (/^https?:\/\//i.test(raw)) return new URL(raw).hostname;
  } catch { /* Eingabe unveraendert lassen */ }
  return raw.replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
}

/**
 * Was gespeichert wird: die Id des gewählten Workspace. Freier Text wird
 * zuerst gegen die Namen der Liste aufgelöst (ein getippter Name zählt wie
 * eine Auswahl), sonst als Referenz übernommen — für Ids und Altbestand,
 * die im MDM-Replikat fehlen.
 */
function resolveWorkspaceRef(value: WorkspaceRefOption | string | null, options: WorkspaceRefOption[]): string | null {
  if (!value) return null;
  if (typeof value !== 'string') return value.ref;
  const typed = value.trim();
  if (!typed) return null;
  const byName = options.find((o) => o.name?.toLowerCase() === typed.toLowerCase());
  return byName ? byName.ref : typed;
}

export default function AddDomainDialog({ open, onClose, initialHostname, onCreated }: Props) {
  const [hostname, setHostname] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [workspace, setWorkspace] = useState<WorkspaceRefOption | string | null>(null);
  const [level, setLevel] = useState<CreateLevel>(2);
  const [refs, setRefs] = useState<WorkspaceRefOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateDomainResponse | null>(null);

  useEffect(() => {
    if (!open) return;
    setHostname(toHostname(initialHostname ?? ''));
    setDisplayName('');
    setWorkspace(null);
    setLevel(2);
    setError(null);
    setResult(null);
    api.get<WorkspaceRefOption[]>('/watchdog/workspace-refs').then((res) => {
      if (res.success) setRefs(res.data);
    });
  }, [open, initialHostname]);

  const workspaceRef = resolveWorkspaceRef(workspace, refs);
  const hasNames = refs.some((r) => r.name);

  async function submit() {
    if (!hostname.trim()) { setError('Domain ist ein Pflichtfeld.'); return; }
    if (!workspaceRef) { setError('Workspace ist ein Pflichtfeld.'); return; }
    setBusy(true); setError(null);
    const res = await api.post<CreateDomainResponse>('/watchdog/domains', {
      hostname: hostname.trim(),
      display_name: displayName.trim() || null,
      workspace_ref: workspaceRef,
      level,
    });
    setBusy(false);
    if (!res.success) { setError(res.error.message); return; }
    setResult(res.data);
    onCreated?.(res.data);
  }

  const levelHint = CREATE_LEVELS.find((l) => l.level === level)?.hint;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Domain hinzufügen</DialogTitle>

      <DialogContent dividers>
        {result ? (
          <ResultView result={result} />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <TextField
              label="Domain (hostname)" required size="small" value={hostname}
              onChange={(e) => setHostname(e.target.value)}
              onBlur={() => setHostname(toHostname(hostname))}
              placeholder="www.beispiel.de"
              helperText="Volladresse wie location.hostname"
            />

            <TextField
              label="Anzeigename" size="small" value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={hostname || 'Beispiel GmbH'}
              helperText="Optional — leer übernimmt den Hostname"
            />

            <Autocomplete
              freeSolo options={refs} value={workspace}
              getOptionLabel={(o) => (typeof o === 'string' ? o : (o.name ?? o.ref))}
              isOptionEqualToValue={(o, v) => o.ref === (typeof v === 'string' ? v : v.ref)}
              // Suche über Name UND Id — wer die Zahl kennt, findet den Workspace auch damit.
              filterOptions={(options, state) => {
                const q = state.inputValue.trim().toLowerCase();
                if (!q) return options;
                return options.filter((o) => (o.name ?? '').toLowerCase().includes(q) || o.ref.toLowerCase().includes(q));
              }}
              renderOption={(props, o) => {
                const { key, ...rest } = props;
                return (
                  <li key={key} {...rest}>
                    <Box>
                      <Typography variant="body2">{o.name ?? o.ref}</Typography>
                      <Typography variant="caption" sx={{ color: colors.black60 }}>
                        {o.name ? `Workspace-Id ${o.ref}` : 'nur als Referenz vorhanden — kein Name im MDM-Replikat'}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              onChange={(_e, v) => setWorkspace(v)}
              onInputChange={(_e, v, reason) => { if (reason === 'input') setWorkspace(v); }}
              renderInput={(params) => (
                <TextField
                  {...params} required label="Workspace" size="small"
                  helperText={
                    !hasNames
                      ? 'Keine Workspace-Namen verfügbar (MDM-Replikat nicht lesbar) — Referenz eintippen'
                      : workspaceRef
                        ? `Gespeichert wird die Workspace-Id ${workspaceRef}`
                        : 'Workspace nach Name wählen — gespeichert wird seine Id'
                  }
                />
              )}
            />

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Anlege-Ebene</Typography>
              <RadioGroup value={level} onChange={(e) => setLevel(Number(e.target.value) as CreateLevel)}>
                {CREATE_LEVELS.map((l) => (
                  <FormControlLabel
                    key={l.level} value={l.level} control={<Radio size="small" />}
                    label={<Typography variant="body2">{l.label}</Typography>}
                  />
                ))}
              </RadioGroup>
              <Typography variant="caption" sx={{ color: colors.black60 }}>{levelHint}</Typography>
            </Box>

            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {result ? (
          <Button variant="contained" onClick={onClose}
            sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8e600' } }}>
            Schließen
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy} sx={{ color: colors.black85 }}>Abbrechen</Button>
            <Button
              variant="contained" onClick={submit} disabled={busy}
              startIcon={busy ? <CircularProgress size={16} sx={{ color: colors.black }} /> : <AddCircleOutlineIcon />}
              sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8e600' }, '&.Mui-disabled': { bgcolor: colors.lightGrey } }}
            >
              {level === 1 ? 'Anlegen' : level === 2 ? 'Anlegen & GTM auslesen' : 'Anlegen & Discovery starten'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** Erfolgsmeldung mit Zusammenfassung (PRD Kap. 4.4: „Info muss valide ankommen"). */
function ResultView({ result }: { result: CreateDomainResponse }) {
  const p = result.profile;
  return (
    <Box sx={{ pt: 1 }}>
      {result.onboarding_status === 'failed' ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Domain angelegt, aber der Discovery Run konnte nicht eingeplant werden:
          {' '}{result.dispatch_error ?? 'unbekannter Grund'}
        </Alert>
      ) : result.level === 3 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Discovery Run läuft — der Status wechselt auf <b>aktiv</b>, sobald die Analyse zurückkommt.
          {result.discovery_mode === 'direct' && (
            <> Der Scan dauert etwa 90–120 Sekunden; die Liste zeigt den neuen Stand nach einem Neuladen.</>
          )}
        </Alert>
      ) : (
        <Alert severity="success" sx={{ mb: 2 }}>Domain angelegt.</Alert>
      )}

      <Typography variant="body2" sx={{ mb: 1 }}>
        <b>{result.hostname}</b>
        {p && <> · {p.events_written} Events · {p.targets_written} Targets{p.unclear_events > 0 && <> · {p.unclear_events} unclear</>}</>}
      </Typography>

      {result.container?.id && (
        <Typography variant="body2" sx={{ color: colors.black60 }}>
          Container: {result.container.id}{result.container.version ? ` (Version ${result.container.version})` : ''}
        </Typography>
      )}

      <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip size="small" label={`Status: ${result.onboarding_status}`} />
        <Chip size="small" label={`Ebene ${result.level}`} variant="outlined" />
        {p && p.unclear_events > 0 && (
          <Chip size="small" variant="outlined" label={`${p.unclear_events} Event(s) nicht simulierbar`}
            sx={{ borderColor: colors.scoreYellow, color: colors.scoreYellow }} />
        )}
      </Box>
    </Box>
  );
}
